/**
 * `<BrandLogo>` — the single source of truth for the OneDial Africa wordmark.
 *
 * Every place in the app (navbar, footer, login, dashboard, dialer, auth
 * shells, admin panel, email previews) renders the logo through this
 * component. Update the file paths or default `alt` text once here and the
 * change propagates everywhere.
 *
 * Variants:
 *   - "wordmark"      Full horizontal wordmark on a light background (default).
 *   - "wordmark-dark" Same wordmark, white-text version for dark backgrounds.
 *   - "mark"          The circular logomark only — used for square avatars,
 *                     mobile app icon previews and tight spots.
 *
 * Sizing: pass any Tailwind height class via `className` (e.g. `h-9`). The
 * image's aspect ratio is preserved automatically.
 *
 * Source files live in `/public/assets/brand/`:
 *   logo-wordmark.png  (transparent bg, original colours)
 *   logo-dark.png      (white text + green accent on navy fill)
 *   mark.png           (square icon — circular badge + green leaf)
 */
const SOURCES = {
  wordmark: "/assets/brand/logo-wordmark.png",
  "wordmark-dark": "/assets/brand/logo-dark.png",
  mark: "/assets/brand/mark.png",
};

const FALLBACK_ALT = "OneDial Africa";

export default function BrandLogo({
  variant = "wordmark",
  className = "h-9 w-auto",
  alt = FALLBACK_ALT,
  ...rest
}) {
  const src = SOURCES[variant] || SOURCES.wordmark;
  return (
    <img
      src={src}
      alt={alt}
      width={variant === "mark" ? 40 : 220}
      height={40}
      decoding="async"
      loading="eager"
      className={className}
      {...rest}
    />
  );
}

export const BRAND = {
  name: "OneDial Africa",
  legalName: "Scitygate Telecom UK Ltd",
  legalNumber: "16468736",
  legalLine: "A trading name of Scitygate Telecom UK Ltd · Co. No. 16468736",
  // Registered office (companies house). Update here only — every page reads
  // these fields so a single change propagates site-wide + legal pages.
  registeredOffice: {
    line1: "2nd Floor, Halifax House",
    line2: "93–101 Bridge Street",
    city:  "Manchester",
    postcode: "M3 2GX",
    country: "United Kingdom",
  },
  // Convenience one-liner — used in meta descriptions & footer fall-back.
  registeredOfficeOneLine:
    "2nd Floor, Halifax House, 93–101 Bridge Street, Manchester M3 2GX, United Kingdom",
  // Absolute URLs are used by SEO meta tags & JSON-LD.
  ogImage: "https://www.onedialafrica.com/assets/brand/og-image.png",
  logoUrl: "https://www.onedialafrica.com/assets/brand/logo-wordmark.png",
};
